import { ClockInSVG, PlusIconSVG } from "@/assets/svg";
import { SelectWithRadio } from "@/components/form";
import AppLayout from "@/components/layout/AppLayout";
import NoDataContainer from "@/components/NoDataContainer";
import ProjectSummary from "@/components/ProjectSummary";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { RootState } from "@/store";
import { getProjectDetail } from "@/store/slices/projectSlice";
import {
  createTimeEntry,
  fetchProjectOptions,
  fetchServicesFilterList,
  resetAllState,
  resetFilters,
  resetForm,
  resetProject,
  selectProjectId,
  selectProjectOptions,
  selectSelectedProject,
  selectServiceOptions,
  selectTeamMemberId,
  selectTimeEntryError,
  selectTimeEntryLoading,
  setProject,
  setTeamMember,
} from "@/store/slices/timeEntrySlice";
import {
  selectActiveEntry,
  selectIsTimerActive,
  startTimer as startTimerAction,
} from "@/store/slices/timerSlice";
import { fetchUserOptions, selectUserOptions } from "@/store/slices/userSlice";
import { ProjectList } from "@/types/project.model";
import { capitalizeFirstLetter, ROLES } from "@/utils/constants";
import { errorToast, successToast } from "@/utils/toast";
import moment from "moment";
import React, { useEffect, useState } from "react";
import ActiveTimeEntry from "./components/ActiveTimeEntry";
import TimeEntryTable, { TimeEntryRow } from "./components/TimeEntryTable";
import { useSelector } from "react-redux";
import { selectUserRole } from "@/store/slices/authSlice";
import Modal from "@/components/Modal";

const TimeEntryMain: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state: RootState) => state.auth);
  const userRole = useSelector(selectUserRole);

  const isAdminOrClient = userRole === ROLES.ADMIN || userRole === ROLES.CLIENT;

  const users = useAppSelector(selectUserOptions);
  const projectOptions = useAppSelector(selectProjectOptions);
  const serviceOptions = useAppSelector(selectServiceOptions);

  // Get time entry state from Redux
  const teamMemberId = useAppSelector(selectTeamMemberId);
  const projectId = useAppSelector(selectProjectId);
  const selectedProject = useAppSelector(selectSelectedProject);
  const loading = useAppSelector(selectTimeEntryLoading);
  // const error = useAppSelector(selectTimeEntryError);

  // Manage entries at component state level instead of Redux
  const [entries, setEntries] = useState<TimeEntryRow[]>([
    {
      id: "1",
      clockInDate: "",
      clockInTime: "",
      clockOutDate: "",
      clockOutTime: "",
      duration: "00:00",
      serviceItem: "",
      billable: false,
      notes: "",
      actions: "",
    },
  ]);
  const [activeEntryId, setActiveEntryId] = useState<string>("1");

  // Get active time entry from Redux
  const isTimerActive = useAppSelector(selectIsTimerActive);
  const reduxActiveEntry = useAppSelector(selectActiveEntry);

  // State for API errors - used for form submission errors
  const [, setApiError] = useState<string | null>(null);
  const [isExceedingHoursModalOpen, setIsExceedingHoursModalOpen] =
    useState<boolean>(false);
  // Store valid entries for submission when exceeding hours
  const [entriesToSubmit, setEntriesToSubmit] = useState<TimeEntryRow[]>([]);

  // Fetch data on component mount
  useEffect(() => {
    // dispatch(fetchProjects({ page: 1, pageSize: 100 }));
    if (isAdminOrClient) {
      dispatch(fetchUserOptions({ showAll: false }));
    } else {
      dispatch(setTeamMember(user?.id as string));
    }
    dispatch(fetchServicesFilterList());
  }, [isAdminOrClient, user]);

  useEffect(() => {
    dispatch(fetchProjectOptions({ search: "", userId: teamMemberId }));
  }, [teamMemberId]);

  // Reset state when team member or project changes
  useEffect(() => {
    return () => {
      dispatch(resetFilters());
    };
  }, []);

  // Handle team member selection
  const handleTeamMemberChange = (value: string) => {
    dispatch(setTeamMember(value));
    dispatch(resetProject());
    setEntries([
      {
        id: "1",
        clockInDate: "",
        clockInTime: "",
        clockOutDate: "",
        clockOutTime: "",
        duration: "00:00",
        serviceItem: "",
        billable: false,
        notes: "",
        actions: "",
      },
    ]);
  };

  // Handle project selection
  const handleProjectChange = (projectId: string) => {
    // Fetch the latest project details from the API instead of using table data
    dispatch(getProjectDetail(projectId))
      .unwrap()
      .then((projectData: ProjectList) => {
        dispatch(setProject({ projectId, projectData }));
      })
      .catch((error: Error) => {
        console.error("Failed to fetch project details:", error);
      });
  };

  // Calculate duration between two date-time combinations
  const calculateDuration = (
    inDate: string,
    inTime: string,
    outDate: string,
    outTime: string
  ): string => {
    // Return 00:00 if any of the values are missing
    if (!inDate || !inTime || !outDate || !outTime) {
      return "00:00";
    }

    try {
      // Parse the date and time strings into moment objects
      const clockIn = moment(`${inDate} ${inTime}`, "YYYY-MM-DD hh:mm A");
      const clockOut = moment(`${outDate} ${outTime}`, "YYYY-MM-DD hh:mm A");

      // Check if both dates are valid
      if (!clockIn.isValid() || !clockOut.isValid()) {
        return "00:00";
      }

      // Calculate the duration in minutes
      const durationMinutes = clockOut.diff(clockIn, "minutes");

      // Handle negative durations (if clock out is before clock in)
      if (durationMinutes < 0) {
        return "00:00";
      }

      // Convert to hours and minutes format
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;

      // Format as HH:MM
      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
    } catch (error) {
      console.error("Error calculating duration:", error);
      return "00:00";
    }
  };

  // Handle date and time inputs for a specific entry
  const handleDateTimeChange = (
    entryId: string,
    field: keyof Omit<TimeEntryRow, "id">,
    value: string
  ) => {
    setEntries((prevEntries) =>
      prevEntries.map((entry) => {
        if (entry.id === entryId) {
          const updatedEntry = { ...entry, [field]: value };

          // Recalculate duration if all date/time fields are filled
          const { clockInDate, clockInTime, clockOutDate, clockOutTime } =
            updatedEntry;
          if (clockInDate && clockInTime && clockOutDate && clockOutTime) {
            updatedEntry.duration = calculateDuration(
              clockInDate,
              clockInTime,
              clockOutDate,
              clockOutTime
            );
          }

          return updatedEntry;
        }
        return entry;
      })
    );

    // We're not updating existing entries in the API as this is only for key-in
  };

  // Handle service item selection for a specific entry
  const handleServiceItemChange = (entryId: string, value: string) => {
    setEntries((prevEntries) =>
      prevEntries.map((entry) =>
        entry.id === entryId ? { ...entry, serviceItem: value } : entry
      )
    );

    // We're not updating existing entries in the API as this is only for key-in
  };

  // Handle billable checkbox for a specific entry
  const handleBillableChange = (entryId: string, checked: boolean) => {
    setEntries((prevEntries) =>
      prevEntries.map((entry) =>
        entry.id === entryId ? { ...entry, billable: checked } : entry
      )
    );

    // We're not updating existing entries in the API as this is only for key-in
  };

  // Handle notes input for a specific entry
  const handleNotesChange = (entryId: string, value: string) => {
    setEntries((prevEntries) =>
      prevEntries.map((entry) =>
        entry.id === entryId ? { ...entry, notes: value } : entry
      )
    );

    // We're not updating existing entries in the API as this is only for key-in
  };

  // Handle clock in button click
  const handleClockIn = () => {
    // Ensure team member and project are selected before starting timer
    if (!teamMemberId || !projectId) {
      console.error(
        "Team member and project must be selected before starting timer"
      );
      return;
    }

    const now = moment();
    const formattedDate = now.format("MM/DD/YYYY");
    const formattedTime = now.format("HH:mm");

    // Create a new active time entry
    const newActiveEntry = {
      id: `temp-${Date.now()}`,
      clockInDate: formattedDate,
      clockInTime: formattedTime,
      clockOutDate: "",
      clockOutTime: "",
      duration: "00:00",
      serviceItem: "",
      billable: true,
      notes: "",
    };

    // First ensure we have the latest team member and project values stored
    // This prevents any race conditions with state updates
    if (teamMemberId && projectId && selectedProject) {
      // Then start the timer
      dispatch(startTimerAction(newActiveEntry));
    }
  };

  // Check if duration exceeds remaining hours
  const checkIfDurationExceedsRemainingHours = (entries: TimeEntryRow[]) => {
    if (!selectedProject) return false;

    // Calculate total duration from all entries
    const totalDurationMinutes = entries.reduce((total, entry) => {
      const durationParts = entry.duration?.split(":") || ["0", "0"];
      const hours = parseInt(durationParts[0], 10) || 0;
      const minutes = parseInt(durationParts[1], 10) || 0;
      return total + (hours * 60 + minutes);
    }, 0);

    // Convert to hours
    const totalDurationHours = totalDurationMinutes / 60;

    // Get remaining hours from project
    const remainingHours =
      selectedProject.tracker?.total_esti_hrs - selectedProject.util_hrs;

    // Check if duration exceeds remaining hours
    return totalDurationHours > remainingHours;
  };

  // Handle submit button click
  const handleSubmit = () => {
    // First check if all entries have service items
    const entriesWithoutService = entries.filter(
      (entry) =>
        (entry.clockInDate &&
          entry.clockInTime &&
          entry.clockOutDate &&
          entry.clockOutTime &&
          entry.duration !== "00:00" &&
          !entry.serviceItem) ||
        (entry.clockInDate &&
          entry.duration &&
          entry.duration !== "00:00" &&
          !entry.serviceItem)
    );

    if (entriesWithoutService.length > 0) {
      errorToast("Error", "Service item is required for all entries");
      return;
    }

    // Validate entries
    const validEntries = entries.filter(
      (entry) =>
        // Case 1: All time fields are filled
        (entry.clockInDate &&
          entry.clockInTime &&
          entry.clockOutDate &&
          entry.clockOutTime &&
          entry.duration !== "00:00" &&
          entry.serviceItem) ||
        // Case 2: Clock in date and duration are filled
        (entry.clockInDate &&
          entry.duration &&
          entry.duration !== "00:00" &&
          entry.serviceItem)
    );

    if (validEntries.length === 0) {
      errorToast("Error", "No valid entries to submit");
      return;
    }

    // Check if duration exceeds remaining hours
    if (checkIfDurationExceedsRemainingHours(validEntries)) {
      // Store entries for later submission if user confirms
      setEntriesToSubmit(validEntries);
      // Show exceeding hours modal
      setIsExceedingHoursModalOpen(true);
      return;
    }

    // If not exceeding, submit directly
    submitEntries(validEntries);
  };

  // Function to submit entries
  const submitEntries = (entriesToSubmit: TimeEntryRow[]) => {
    // Create promises for each entry
    const submitPromises = entriesToSubmit.map((entry) => {
      let timeEntryPayload: TimeEntryRow = {
        ...entry,
        entry: "MANUAL",
      };

      return dispatch(createTimeEntry(timeEntryPayload)).unwrap();
    });

    // Execute all promises
    Promise.all(submitPromises)
      .then(() => {
        successToast("Success", "Time entries submitted successfully");
        // Reset entries after successful submission
        setEntries([
          {
            id: "1",
            clockInDate: "",
            clockInTime: "",
            clockOutDate: "",
            clockOutTime: "",
            duration: "00:00",
            serviceItem: "",
            billable: false,
            notes: "",
            actions: "",
          },
        ]);
        setActiveEntryId("1");

        handleProjectChange(projectId);
      })
      .catch((err) => {
        setApiError(err || "Failed to submit time entries");
        // errorToast("Error", err || "Failed to submit time entries");
      });
  };

  // Handle form cancellation
  const handleCancel = () => {
    // Reset entries to initial state
    setEntries([
      {
        id: "1",
        clockInDate: "",
        clockInTime: "",
        clockOutDate: "",
        clockOutTime: "",
        duration: "00:00",
        serviceItem: "",
        billable: false,
        notes: "",
        actions: "",
      },
    ]);
    setActiveEntryId("1");

    // Reset Redux state
    // Set team member back to current user if available

    if (isAdminOrClient) {
      dispatch(resetForm());
      dispatch(setTeamMember(""));
    } else {
      dispatch(resetProject());
    }
    // Clear project selection
    dispatch(setProject({ projectId: "", projectData: null }));
  };

  // Handle adding a new entry
  const handleAddEntry = () => {
    const newEntry: TimeEntryRow = {
      id: `entry-${Date.now()}`,
      clockInDate: "",
      clockInTime: "",
      clockOutDate: "",
      clockOutTime: "",
      duration: "00:00",
      serviceItem: "",
      billable: false,
      notes: "",
      actions: "",
    };
    setEntries((prevEntries) => [...prevEntries, newEntry]);
  };

  // Handle delete entry - only for local entries since we're not fetching from API
  const handleDeleteEntry = (entryId: string) => {
    setEntries((prevEntries) => {
      // Filter out the entry to be deleted
      const filteredEntries = prevEntries.filter(
        (entry) => entry.id !== entryId
      );

      // If this would result in no entries, reset to initial state with one empty entry
      if (filteredEntries.length === 0) {
        return [
          {
            id: "1",
            clockInDate: "",
            clockInTime: "",
            clockOutDate: "",
            clockOutTime: "",
            duration: "00:00",
            serviceItem: "",
            billable: false,
            notes: "",
            actions: "",
          },
        ];
      }

      // Otherwise return the filtered entries
      return filteredEntries;
    });

    // If we're deleting the active entry, set the first remaining entry as active
    if (entryId === activeEntryId) {
      setEntries((prevEntries) => {
        if (prevEntries.length > 0) {
          setActiveEntryId(prevEntries[0].id);
        }
        return prevEntries;
      });
    }
  };

  // Service items options (mock data)
  const serviceItems = serviceOptions.map((option) => ({
    value: option,
    label: option,
  }));

  const teamMemberOptions = !isAdminOrClient
    ? [{ value: user?.id as string, label: `${user?.name} (you)` as string }]
    : users.map((userData) => ({
        value: userData.id,
        label:
          user?.id === userData.id
            ? `${userData.full_name} (you)`
            : userData.full_name,
      }));

  return (
    <AppLayout title="Time entry">
      <div className="p-6 bg-white rounded-lg shadow-md min-h-[800px]">
        {/* Selection fields */}
        <p className="text-[14px] text-[#6C6C6C] mb-2">
          <span className="text-[#DC2626]">*</span> indicates mandatory fields
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <SelectWithRadio
              label="Team member"
              required
              maxHeight="300px"
              options={teamMemberOptions}
              showAbove={false}
              value={teamMemberId}
              onChange={handleTeamMemberChange}
              placeholder="Select team member"
              disabled={isTimerActive || !isAdminOrClient}
            />
          </div>
          <div>
            <SelectWithRadio
              label="Project"
              required
              options={projectOptions.map((project) => ({
                label: project.display_name,
                value: project.id,
              }))}
              showAbove={false}
              value={projectId}
              onChange={handleProjectChange}
              placeholder="Select project"
              disabled={isTimerActive}
            />
          </div>
        </div>

        {/* Project summary section */}
        {selectedProject && teamMemberId ? (
          <>
            <ProjectSummary
              clientName={
                capitalizeFirstLetter(selectedProject.client?.org_name) || ""
              }
              projectId={selectedProject.id}
              startDate={selectedProject.start_date}
              endDate={selectedProject.end_date}
              hoursEstimated={selectedProject.tracker?.total_esti_hrs}
              hoursUtilized={selectedProject.util_hrs || 0}
              status={capitalizeFirstLetter(selectedProject.status)}
              remainingHours={selectedProject.remi_hrs?.toString() || "0"}
            />

            {/* Track time section */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-[16px] font-medium">Track time</h3>
                {!isTimerActive && teamMemberId === user?.id && (
                  <button
                    onClick={handleClockIn}
                    className="w-[97px] h-[34px] bg-[#73B5C5] hover:bg-[#5da7b8] text-white px-3 py-2 rounded-md flex items-center cursor-pointer"
                  >
                    <span className="mr-1 text-[14px]">Clock in</span>
                    <ClockInSVG />
                  </button>
                )}
              </div>

              {/* Render ActiveTimeEntry if there is an active time entry, otherwise render TimeEntryTable */}
              {isTimerActive ? (
                <ActiveTimeEntry
                  initialEntry={
                    reduxActiveEntry || {
                      id: "",
                      clockInDate: "",
                      clockInTime: "",
                      clockOutDate: "",
                      clockOutTime: "",
                      duration: "00:00",
                      serviceItem: "",
                      billable: false,
                      notes: "",
                    }
                  }
                  serviceItems={serviceItems}
                />
              ) : (
                <>
                  <>
                    {/* {error && (
                      <div className="py-4 text-center text-red-500">
                        {error}
                      </div>
                    )} */}
                    <TimeEntryTable
                      entries={entries}
                      serviceItems={serviceItems}
                      onDateTimeChange={handleDateTimeChange}
                      onServiceItemChange={handleServiceItemChange}
                      onBillableChange={handleBillableChange}
                      onNotesChange={handleNotesChange}
                      activeEntryId={activeEntryId}
                      setActiveEntryId={setActiveEntryId}
                      onDeleteEntry={handleDeleteEntry}
                    />
                  </>

                  {/* Add entry button */}
                  <div className="mt-4 flex justify-end">
                    <button
                      className="w-[147px] h-[34px] px-3 bg-[#F5F5F5] text-[#1E1E1E] rounded-[4px] hover:bg-[#f0f9fb] transition-colors flex items-center gap-1 cursor-pointer"
                      onClick={handleAddEntry}
                    >
                      <PlusIconSVG color="#9E9E9E" />
                      Add Entry
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Modal for confirmation */}
            <Modal
              isOpen={isExceedingHoursModalOpen}
              onClose={() => setIsExceedingHoursModalOpen(false)}
              title="Exceeding Hours"
              width="min(95%, 550px)"
            >
              <div className="py-4">
                <p className="text-center text-[18px] font-[500] text-[#212121] mb-6">
                  The clocked time exceeds the project's estimated hours. Do you
                  still want to proceed ?
                </p>
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => {
                      submitEntries(entriesToSubmit);
                      setIsExceedingHoursModalOpen(false);
                    }}
                    className="px-3 py-2 w-[120px] text-[14px] font-medium h-[36px] bg-[#73B5C5] text-white rounded-md hover:bg-[#5da7b8] transition-colors cursor-pointer"
                  >
                    Yes, add time
                  </button>
                  <button
                    onClick={() => setIsExceedingHoursModalOpen(false)}
                    className="px-3 py-2 w-[98px] h-[36px] text-[14px] font-medium bg-[#F5F5F5] text-gray-800 rounded-md hover:bg-gray-200 transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Modal>
          </>
        ) : (
          <NoDataContainer
            title={`${
              isAdminOrClient
                ? "Select team member and project to add time."
                : "Select project to add time."
            }`}
          />
        )}

        {!isTimerActive && (
          <div className="mt-14 py-4 border-t border-gray-200 flex justify-end gap-2 rounded-b-[10px]">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-[14px] w-[147px] text-gray-700 bg-[#F5F5F5] rounded-md hover:bg-gray-50 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-[147px] h-[34px] bg-[#73b5c5] text-white text-[14px] font-[500] border-none rounded-[4px] cursor-pointer transition-colors hover:bg-[#5a9aaa] disabled:bg-[#9e9e9e] disabled:cursor-not-allowed flex items-center justify-center"
              onClick={handleSubmit}
              disabled={
                loading ||
                !teamMemberId ||
                !projectId ||
                entries.length === 0 ||
                !entries.some(
                  (entry) =>
                    // Case 1: clockInDate and duration are filled
                    (entry.clockInDate &&
                      entry.duration &&
                      entry.duration !== "00:00") ||
                    // Case 2: all four time fields are filled (duration will be auto-calculated)
                    (entry.clockInDate &&
                      entry.clockInTime &&
                      entry.clockOutDate &&
                      entry.clockOutTime)
                )
              }
            >
              Add time
            </button>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default TimeEntryMain;
